import * as ApiRequests from '../../api/requests';
import { AuthHelper } from '../../types/auth-helper-types';
import { LocationInfo, makeLoadedData } from '../../types';

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const getLocations = async (
    authHelper: AuthHelper,
    args: { organizationId: string; userId: string; operation: string }
) => {
    const result = await ApiRequests.getLocations(
        {
            organizationId: args.organizationId,
            userId: args.userId,
            operation: args.operation,
        },
        { authHelper }
    );

    if (!result.ok) {
        console.error(ApiRequests.extractErrorCodeWithMessage(result.response));
        throw new Error(ApiRequests.extractErrorCodeWithMessage(result.response));
    }
    const newState = result.response.data.locations.map(
        (obj: LocationInfo): LocationInfo => ({
            ...obj,
            locationName: obj.locationName.charAt(0).toUpperCase() + obj.locationName.slice(1),
        })
    );

    result.response.data.locations = newState;
    return result.response.data;
};

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const getAlarmCountForLocation = async (
    authHelper: AuthHelper,
    locationId: string
) => {
    const locationAlarmCountResult = await ApiRequests.getAlarmCount(
        {
            siteIdOrLocationIdOrOrganizationId: locationId,
        },
        { authHelper }
    );

    if (!locationAlarmCountResult.ok && process.env.NODE_ENV !== 'test') {
        console.error(ApiRequests.extractResponseNotOkMessage(locationAlarmCountResult.response));
        throw new Error(ApiRequests.extractErrorCodeWithMessage(locationAlarmCountResult.response));
    }
    return locationAlarmCountResult.ok ? locationAlarmCountResult.response.data.alarmCount : 0;
};
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const getLocationListWithAlarm = async (
    authHelper: AuthHelper,
    locationList: LocationInfo[]
) => {
    const list = await Promise.all(
        locationList.map(async (locationInfo) => {
            const alarmCountForLocationResult = await getAlarmCountForLocation(
                authHelper,
                locationInfo.locationId
            );
            return {
                locationId: locationInfo.locationId,
                locationName: locationInfo.locationName,
                alarmCount: alarmCountForLocationResult ? alarmCountForLocationResult : 0,
                siteCount: locationInfo.siteCount,
            };
        })
    );

    return list;
};

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const getLocationCardInfo = async (
    authHelper: AuthHelper,
    args: { locationId: string }
) => {
    const sitesResult = await ApiRequests.getSites(
        {
            locationId: args.locationId,
        },
        { authHelper }
    );

    if (!sitesResult.ok) {
        console.error(ApiRequests.extractErrorCodeWithMessage(sitesResult.response));
        throw new Error(ApiRequests.extractErrorCodeWithMessage(sitesResult.response));
    }
    const siteData = sitesResult.ok && sitesResult.response.data.sites 
        ? sitesResult.response.data.sites.map((siteInfo) => ({
              siteId: siteInfo.siteId,
              siteName: siteInfo.siteName,
          }))
        : [];
    return {
        id: args.locationId,
        sites: makeLoadedData(siteData),
        eventCount: undefined,
    };
};

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const getExistingUserLocation = async (
    authHelper: AuthHelper,
    organizationId: string,
    userId: string,
) => {
    const userLocationResult = await ApiRequests.getLocations(
        {
            organizationId: organizationId,
            userId: userId,
            operation: 'view',
        },
        { authHelper }
    );

    if (!userLocationResult.ok) {
        console.error(ApiRequests.extractResponseNotOkMessage(userLocationResult.response));
        throw new Error(ApiRequests.extractResponseNotOkMessage(userLocationResult.response));
    }
    const result: any = {
        id: userLocationResult.response.data.organizationId,
        name: userLocationResult.response.data.organizationName,
        address: userLocationResult.response.data.address,
        locations: userLocationResult.response.data.locations.map((loc) => ({
            id: loc.locationId,
            name: loc.locationName,
            address: loc.address,
        })),
    };
    return result;
};

export {
    getLocations,
    getLocationCardInfo,
    getLocationListWithAlarm,
    getAlarmCountForLocation,
    getExistingUserLocation,
};
